import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import ChatSidebar from '../shared/ChatSidebar';
import { exportMarkdownAsPDF, generateDeploymentMarkdownTable, generateDeploymentPlainTextTable } from '../../utils/exportUtils';
import { openaiClient } from '../../utils/openaiClient';
import './EvoMLDeploymentEmailGenerator.css';

const EvoMLDeploymentEmailGenerator = ({ deploymentOptions }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [productType, setProductType] = useState('evoml'); // 'evoml' or 'artemis'
  const [emailTemplate, setEmailTemplate] = useState('professional'); // 'professional', 'concise', 'technical', 'markdown', 'rawmarkdown'
  const [additionalContext, setAdditionalContext] = useState('');
  const [clientInfo, setClientInfo] = useState({
    company: '',
    contactName: '',
    industry: '',
    teamSize: '',
    useCase: ''
  });
  const [generatedSummary, setGeneratedSummary] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('summary');
  const [includeTable, setIncludeTable] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatSystemPrompt, setChatSystemPrompt] = useState('');
  const [chatUserPrompt, setChatUserPrompt] = useState('');
  const [outputMode, setOutputMode] = useState('instant'); // 'instant' or 'chat'
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState('');

  const handleOptionToggle = (optionId) => {
    setSelectedOptions(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId);
      } else {
        return [...prev, optionId];
      }
    });
    // Clear generated content when options change
    setGeneratedEmail('');
    setGeneratedSummary('');
  };

  const handleInputChange = (field, value) => {
    setClientInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerate = async () => {
    if (selectedOptions.length === 0) {
      setError('Please select at least one deployment option');
      return;
    }

    setError('');
    setIsGenerating(true);

    try {
      const selectedDeployments = deploymentOptions.filter(opt => 
        selectedOptions.includes(opt.id)
      );

      // Generate comparison summary
      let summaryContent = `## ${productType === 'evoml' ? 'EvoML' : 'Artemis'} Deployment Options Comparison\n\n`;
      
      // Add markdown table
      const markdownTable = generateDeploymentMarkdownTable(selectedDeployments);
      summaryContent += markdownTable + '\n\n';
      
      // Add plain text table for copying
      const plainTextTable = generateDeploymentPlainTextTable(selectedDeployments);
      summaryContent += '### Plain Text Version (for copying to email):\n\n```\n' + plainTextTable + '```\n\n';
      
      // Add deployment details
      summaryContent += '### Deployment Details:\n\n';
      selectedDeployments.forEach(opt => {
        summaryContent += `**${opt.title}**\n`;
        summaryContent += `- Best for: ${opt.useCase}\n`;
        summaryContent += `- Security: ${opt.security?.level || 'Standard'}\n`;
        summaryContent += `- Privacy: ${opt.privacy}\n`;
        summaryContent += `- LLM Support: ${opt.llmIntegration?.providers?.join(', ') || 'Various'}\n\n`;
      });
      
      setGeneratedSummary(summaryContent);

      // Prepare chat prompts based on template type
      const productName = productType === 'evoml' ? 'EvoML' : 'Artemis';
      const productDescription = productType === 'evoml' 
        ? 'our data science platform' 
        : 'our code optimization platform';
      
      const productDetails = productType === 'evoml'
        ? `EvoML is our comprehensive data science platform that empowers teams to build, train, and deploy ML models efficiently. It provides advanced AutoML capabilities, experiment tracking, and model management.`
        : `Artemis is our intelligent code optimization platform that uses AI to analyze, optimize, and improve code quality, performance, and security. It helps development teams write better code faster.`;
      
      const llmDetails = `
Key LLM Capabilities:
- Bring Your Own LLM: Choose from OpenAI, AWS Bedrock, Azure OpenAI, Google Vertex AI, Anthropic, or any custom LLM
- No Training on Your Data: In SaaS deployments, all LLMs used do not train on customer data
- Flexible Integration: Add LLMs via API keys or integrate with your enterprise LLM gateways
- Cost Management: Use our LLMs with usage-based pricing or bring your own to manage costs directly
- POC Credits: We provide $50 in LLM credits to start your proof of concept
`;

      const saasDetails = `
SaaS Deployment Details:
- Shared SaaS: Single cluster with encrypted, isolated customer data. Recommended for quick start and ease of use
- Dedicated SaaS: New cluster on dedicated IP with location-based access restrictions for enhanced privacy
- Both options ensure complete data isolation and encryption
`;
      
      const templatePrompts = {
        professional: `You are a professional sales engineer at Turintech. Generate a formal business email for ${productName} deployment options.

${productDetails}

${llmDetails}

${saasDetails}

Structure:
1. Professional greeting
2. Brief introduction to ${productName} emphasizing ${productType === 'evoml' ? 'data science capabilities' : 'code optimization benefits'}
3. Simple markdown table comparing deployment options (NO pricing column, but mention POC credits where appropriate)
4. Highlight LLM flexibility and data privacy guarantees
5. Recommend shared SaaS for quick start unless specific privacy requirements exist
6. Professional closing with clear next steps`,
        
        concise: `You are a technical consultant at Turintech. Generate a brief, to-the-point email for ${productName} deployment options.

${productDetails}

Key Points:
- Full LLM flexibility (bring your own or use ours)
- No training on customer data
- $50 POC credits available
- Shared SaaS recommended for quick start

Structure:
1. Direct greeting
2. One-line intro about ${productName}
3. Simple comparison table (NO pricing column)
4. 3 key differentiators including LLM flexibility
5. Clear recommendation and call to action`,
        
        technical: `You are a solutions architect at Turintech. Generate a detailed technical email for ${productName} deployment options.

${productDetails}

${llmDetails}

Technical Implementation:
- API Integration: RESTful APIs with SDK support
- LLM Gateway: Support for custom LLM gateways and enterprise API management
- Security: End-to-end encryption, isolated environments, SOC 2 compliance
- On-premise: Full support for air-gapped deployments with local LLMs

${saasDetails}

Structure:
1. Technical greeting
2. Technical overview of ${productName} architecture
3. Detailed comparison table (NO pricing column)
4. Deep dive on LLM integration options and data privacy
5. Security and compliance details
6. Technical implementation roadmap`,
        
        markdown: `Generate a clean markdown document for ${productName} deployment options.

${productDetails}

${llmDetails}

Structure:
# ${productName} Deployment Options
## Overview
Detailed description of ${productName} and its key capabilities
## LLM Integration
- Flexibility to choose any LLM provider
- Bring your own LLM or use Turintech's
- No training on customer data guarantee
- POC credits available
## Deployment Comparison
Markdown table with deployment options
## Key Features by Deployment Type
- Detailed features for each deployment option
- Focus on LLM flexibility and data privacy
## Recommendations
- Shared SaaS for quick start
- Dedicated options for enhanced privacy
## Next Steps
Clear action items and contact information`,
        
        rawmarkdown: `Generate a clean markdown document for ${productName} deployment options suitable for download.

${productDetails}

${llmDetails}

Structure:
# ${productName} Deployment Options
## Overview
Comprehensive description of ${productName}
## LLM Integration & Data Privacy
- Full LLM provider flexibility
- No training on customer data
- Bring your own LLM support
- $50 POC credits included
## Deployment Options Comparison
Detailed markdown table
## Deployment Details
### Shared SaaS (Recommended)
- Quick start, encrypted isolated data
### Dedicated SaaS
- Enhanced privacy with IP restrictions
### On-Premise Options
- Full air-gap support with local LLMs
## Getting Started
- POC process
- Technical requirements
- Contact information`
      };

      const systemPrompt = templatePrompts[emailTemplate] || templatePrompts.professional;

      const userPrompt = `Generate content for a potential client interested in ${productName} (${productDescription}).

Client Information:
- Company: ${clientInfo.company || '[Client Company]'}
- Contact Name: ${clientInfo.contactName || '[Contact Name]'}
- Industry: ${clientInfo.industry || '[Industry]'}
- Team Size: ${clientInfo.teamSize || '[Team Size]'}
- Use Case: ${clientInfo.useCase || '[Use Case]'}

${additionalContext ? `Additional Context:\n${additionalContext}\n\n` : ''}

Selected Deployment Options:
${selectedDeployments.map(opt => `
### ${opt.title} (${opt.subtitle})
- **Security Level**: ${opt.security?.level || 'Standard'}
- **Privacy**: ${opt.privacy}
- **LLM Integration**: ${opt.llmIntegration?.type || 'Standard'}
- **Supported LLM Providers**: ${opt.llmIntegration?.providers?.join(', ') || 'Various'}
- **Data Privacy**: ${opt.llmIntegration?.dataPrivacy || 'Standard privacy controls'}
- **Key Features**: ${opt.features.slice(0, 3).join(', ')}
- **Best For**: ${opt.useCase}
`).join('\n')}

IMPORTANT: 
- Do NOT include any cost or pricing information in the email or table
- Keep the markdown simple and clean
- The table should have these exact columns: Deployment Type | Infrastructure | Setup Time | Security | Privacy | Internet
- Keep table cells concise - use single words where possible (e.g., "High" not "High Security")
- For Setup Time use: Instant, < 1 day, 1-2 days, 2-3 days, 1 week
- For Security use: Standard, Enhanced, High, Maximum
- For Privacy use: Limited, Medium, High, Maximum
- For Internet use: Yes, No, or Conditional
- Mention support for all major cloud providers (AWS, Azure, Google Cloud) where applicable`;

      setChatSystemPrompt(systemPrompt);
      setChatUserPrompt(userPrompt);
      
      if (outputMode === 'instant') {
        // For instant mode, generate the email directly
        try {
          const response = await openaiClient.generateDeploymentEmail(
            selectedDeployments,
            clientInfo,
            productType,
            emailTemplate,
            additionalContext
          );
          
          setGeneratedSummary(summaryContent);
          setGeneratedEmail(response);
          setActiveTab('email');
          setIsChatOpen(false);
        } catch (error) {
          setError('Failed to generate email: ' + error.message);
        }
      } else {
        // For chat mode, open the chat sidebar
        setActiveTab('email');
        setIsChatOpen(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to prepare content generation');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleChatExport = (format, content) => {
    switch (format) {
      case 'copy':
        navigator.clipboard.writeText(content);
        break;
      case 'markdown':
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${productType}-deployment-${emailTemplate}-${new Date().toISOString().split('T')[0]}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        break;
      case 'pdf':
        exportMarkdownAsPDF(content, `${productType}-deployment-proposal.pdf`);
        break;
      default:
        break;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      const button = document.activeElement;
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    });
  };

  return (
    <div className="email-generator-container">
      <h2 className="generator-title">
        <span className="title-icon">📧</span>
        EvoML & Artemis Deployment Email Generator
      </h2>

      {/* Output Mode Selection */}
      <div className="output-mode-selection">
        <h3>Output Mode</h3>
        <div className="mode-options">
          <label className="mode-option">
            <input
              type="radio"
              name="outputMode"
              value="instant"
              checked={outputMode === 'instant'}
              onChange={(e) => setOutputMode(e.target.value)}
            />
            <span className="mode-label">
              <span className="mode-icon">⚡</span>
              Instant Generation - Generate directly in the right panel
            </span>
          </label>
          <label className="mode-option">
            <input
              type="radio"
              name="outputMode"
              value="chat"
              checked={outputMode === 'chat'}
              onChange={(e) => setOutputMode(e.target.value)}
            />
            <span className="mode-label">
              <span className="mode-icon">💬</span>
              Interactive Chat - Refine with AI assistance
            </span>
          </label>
        </div>
      </div>

      <div className="generator-content">
        <div className="left-panel">
          <h3>Select Deployment Options</h3>
          <div className="options-grid">
            {deploymentOptions.map(option => (
              <div
                key={option.id}
                className={`option-card ${selectedOptions.includes(option.id) ? 'selected' : ''}`}
                onClick={() => handleOptionToggle(option.id)}
              >
                <div className="option-header" style={{ backgroundColor: option.color }}>
                  <span className="option-icon">{option.icon}</span>
                  <h4>{option.title}</h4>
                </div>
                <div className="option-details">
                  <p className="option-subtitle">{option.subtitle}</p>
                  <p className="option-security">Security: {option.security?.level || 'Standard'}</p>
                </div>
                <div className="selection-indicator">
                  {selectedOptions.includes(option.id) ? '✓' : ''}
                </div>
              </div>
            ))}
          </div>

          <h3>Product Selection</h3>
          <div className="product-selection">
            <label className="radio-label">
              <input
                type="radio"
                name="product"
                value="evoml"
                checked={productType === 'evoml'}
                onChange={(e) => setProductType(e.target.value)}
              />
              EvoML - AI/ML Platform
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="product"
                value="artemis"
                checked={productType === 'artemis'}
                onChange={(e) => setProductType(e.target.value)}
              />
              Artemis - Code Optimization Platform
            </label>
          </div>

          <h3>Email Template Style</h3>
          <div className="template-selection">
            <label className="radio-label">
              <input
                type="radio"
                name="template"
                value="professional"
                checked={emailTemplate === 'professional'}
                onChange={(e) => setEmailTemplate(e.target.value)}
              />
              Professional - Formal business email
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="template"
                value="concise"
                checked={emailTemplate === 'concise'}
                onChange={(e) => setEmailTemplate(e.target.value)}
              />
              Concise - Brief and to the point
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="template"
                value="technical"
                checked={emailTemplate === 'technical'}
                onChange={(e) => setEmailTemplate(e.target.value)}
              />
              Technical - Detailed technical overview
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="template"
                value="markdown"
                checked={emailTemplate === 'markdown'}
                onChange={(e) => setEmailTemplate(e.target.value)}
              />
              Markdown - Clean documentation format
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="template"
                value="rawmarkdown"
                checked={emailTemplate === 'rawmarkdown'}
                onChange={(e) => setEmailTemplate(e.target.value)}
              />
              Raw Markdown - Download as .md file
            </label>
          </div>

          <h3>Client Information (Optional)</h3>
          <div className="client-form">
            <input
              type="text"
              placeholder="Company Name"
              value={clientInfo.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
            />
            <input
              type="text"
              placeholder="Contact Name"
              value={clientInfo.contactName}
              onChange={(e) => handleInputChange('contactName', e.target.value)}
            />
            <input
              type="text"
              placeholder="Industry"
              value={clientInfo.industry}
              onChange={(e) => handleInputChange('industry', e.target.value)}
            />
            <input
              type="text"
              placeholder="Team Size"
              value={clientInfo.teamSize}
              onChange={(e) => handleInputChange('teamSize', e.target.value)}
            />
            <textarea
              placeholder="Use Case / Requirements"
              value={clientInfo.useCase}
              onChange={(e) => handleInputChange('useCase', e.target.value)}
              rows="3"
            />
          </div>

          <h3>Additional Context (Optional)</h3>
          <div className="additional-context">
            <textarea
              placeholder="Provide any additional information about the client, their specific needs, pain points, or any other context that would help personalize the email..."
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              rows="4"
              className="context-textarea"
            />
          </div>

          <div className="options-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={includeTable}
                onChange={(e) => setIncludeTable(e.target.checked)}
              />
              Include comparison table in email
            </label>
          </div>

          <button
            className="generate-button"
            onClick={handleGenerate}
            disabled={selectedOptions.length === 0 || isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate Content'}
          </button>

          {error && <div className="error-message">{error}</div>}
        </div>

        <div className="right-panel">
          <div className="output-tabs">
            <button
              className={`tab-button ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
            >
              Comparison Summary
            </button>
            <button
              className={`tab-button ${activeTab === 'email' ? 'active' : ''}`}
              onClick={() => setActiveTab('email')}
            >
              Email/Document
            </button>
          </div>

          <div className="output-content">
            {activeTab === 'summary' && (
              <>
                {generatedSummary ? (
                  <>
                    <div className="output-actions">
                      <button
                        className="copy-button"
                        onClick={() => {
                          // Extract plain text table from summary
                          const plainTextMatch = generatedSummary.match(/```\n([\s\S]*?)```/);
                          if (plainTextMatch) {
                            copyToClipboard(plainTextMatch[1]);
                          } else {
                            copyToClipboard(generatedSummary);
                          }
                        }}
                      >
                        Copy Table as Text
                      </button>
                      <button
                        className="copy-button"
                        onClick={() => {
                          // Copy the entire markdown content
                          copyToClipboard(generatedSummary);
                        }}
                      >
                        Copy All (Markdown)
                      </button>
                      <button
                        className="copy-button text-button"
                        onClick={() => {
                          // Extract and download plain text table
                          const plainTextMatch = generatedSummary.match(/```\n([\s\S]*?)```/);
                          const textContent = plainTextMatch ? plainTextMatch[1] : generatedSummary;
                          const blob = new Blob([textContent], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${productType}-deployment-table-${new Date().toISOString().split('T')[0]}.txt`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }}
                      >
                        Download Table (.txt)
                      </button>
                      <button
                        className="copy-button pdf-button"
                        onClick={() => exportMarkdownAsPDF(generatedSummary, `${productType}-deployment-comparison.pdf`)}
                      >
                        Download as PDF
                      </button>
                    </div>
                    <div className="generated-content markdown-content">
                      <ReactMarkdown>{generatedSummary}</ReactMarkdown>
                    </div>
                  </>
                ) : (
                  <div className="placeholder">
                    <p>Select deployment options and click "Generate Content" to create a comparison summary</p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'email' && (
              <>
                {generatedEmail ? (
                  <>
                    <div className="output-actions">
                      <button
                        className="copy-button"
                        onClick={() => copyToClipboard(generatedEmail)}
                      >
                        Copy Email
                      </button>
                      {emailTemplate === 'rawmarkdown' && (
                        <button
                          className="copy-button markdown-button"
                          onClick={() => {
                            const blob = new Blob([generatedEmail], { type: 'text/markdown' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${productType}-deployment-${new Date().toISOString().split('T')[0]}.md`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          }}
                        >
                          Download as .md
                        </button>
                      )}
                      <button
                        className="copy-button pdf-button"
                        onClick={() => exportMarkdownAsPDF(generatedEmail, `${productType}-deployment-email.pdf`)}
                      >
                        Export as PDF
                      </button>
                    </div>
                    <div className="generated-content markdown-content">
                      <ReactMarkdown>{generatedEmail}</ReactMarkdown>
                    </div>
                  </>
                ) : (
                  <div className="placeholder">
                    <p>
                      {outputMode === 'instant' 
                        ? 'Click "Generate Content" to generate your email/document directly'
                        : 'Click "Generate Content" to open the AI assistant and create your email/document'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ChatSidebar
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        initialSystemPrompt={chatSystemPrompt}
        initialUserPrompt={chatUserPrompt}
        title={`${productType === 'evoml' ? 'EvoML' : 'Artemis'} ${emailTemplate === 'rawmarkdown' ? 'Markdown' : 'Email'} Generator`}
        onExport={handleChatExport}
        exportOptions={{
          copy: true,
          markdown: emailTemplate === 'rawmarkdown' || emailTemplate === 'markdown',
          pdf: emailTemplate !== 'rawmarkdown'
        }}
      />
    </div>
  );
};

export default EvoMLDeploymentEmailGenerator;